/**
 * 全プロンプト（ONP 35 + 顧客レポート 24）の集約カタログ。
 *
 * PRODUCT_DEV-356 でハッシュ一覧用に作ったものを、PRODUCT_DEV-708 で
 * **プロンプト編集画面の唯一の正**へ格上げした。settingId ごとの
 * 「機能・表示名・説明・並び順」はすべてここが持ち、画面側は持たない。
 *
 * ## なぜ 1 か所に集約するのか
 * 以前は表示名が `MeetingSettings.tsx` / `ReportSettings.tsx` / `HashList` 用カタログに
 * 三重定義され、編集導線の一覧（subTabEditorIds）はさらに別だった。その結果
 * PRODUCT_DEV-612 で「enum にはあるが Admin から編集できない」キーが 7 件、
 * 「カタログから漏れてハッシュ一覧にも出ない」キーが 1 件、放置されていた。
 *
 * ## 網羅性の担保
 * `FEATURE_MEMBERS` は `as const satisfies` + `AssertNever` で **全 settingId が
 * ちょうど 1 つの機能に属すること**を型で強制する。enum にキーを足してここに
 * 載せ忘れるとコンパイルエラーになる。
 */
import type { ONPPromptSettingId } from "@shared-domain/admin/contract";
import {
  ONPPromptSettingIdSchema,
  ALL_REPORT_PROMPT_SETTING_IDS,
  REPORT_PROMPT_TYPES,
  getReportPromptSettingId,
  type ReportPromptType,
} from "@shared-domain/admin/contract";
import { REPORT_CATEGORIES } from "@shared-domain/unizo/contract";

/**
 * 「機能」= 編集画面の第 1 階層。URL に載るので ASCII のスラッグにする。
 * 分類は PRODUCT_DEV-708 以前のサブタブ構成をそのまま引き継いでいる。
 */
export type PromptFeature =
  | "recording"
  | "summary"
  | "assist"
  | "gatekeeper"
  | "minutes"
  | "review"
  | "search"
  | "report"
  | "base";

export interface PromptFeatureMeta {
  id: PromptFeature;
  label: string;
  /** 機能ボタンを選んだとき、カード未選択の右ペインに出す説明 */
  description: string;
}

/**
 * 機能ボタンの表示順。
 *
 * 土台（ONP基盤）を左端に置き、そこから商談記録の一連の流れ
 * （録音 → 録音後の抽出/補完 → 要約 → 入力補助）、次に議事録まわり
 * （議事録 → 振り返り）、最後に検索とレポートという並び。
 *
 * この配列の順序は**カタログの並び順そのもの**でもあるため、
 * ハッシュ一覧の行順もこれに追従する。
 */
export const PROMPT_FEATURES: PromptFeatureMeta[] = [
  {
    id: "base",
    label: "ONP基盤",
    description:
      "全プロンプトの土台になる Layer 1 / Layer 2。ここを変えると全機能に影響するため、変更は慎重に。",
  },
  {
    id: "recording",
    label: "商談記録",
    description:
      "録音中の AI の振る舞いを決めるプロンプト群。プリセットを持たないユーザのベースルール、法人／個人の検索ルール、コピーモードの指示、対話キャラクターのトーンなど。",
  },
  {
    id: "gatekeeper",
    label: "GateKeeper（補完）",
    description:
      "録音後に走る抽出・チェック・補完のプロンプト群。項目抽出 → グローバルチェック → ヒアリングメモ抽出の順に実行される。",
  },
  {
    id: "summary",
    label: "要約",
    description:
      "商談グループの要約と、日報／週報／月報の活動サマリを生成するプロンプト群。録音そのものには関与しない。",
  },
  {
    id: "assist",
    label: "入力補助",
    description:
      "自由記録モードのガイド生成、プリセットの AI 作成、CRM コピー文の要約、法人名カナ推定など、入力を助けるプロンプト群。",
  },
  {
    id: "minutes",
    label: "議事録",
    description:
      "議事録の文字起こしとその構造化（要約・決定事項・Next アクション）に使うプロンプト群。",
  },
  {
    id: "review",
    label: "振り返り",
    description:
      "議事録詳細の「商談の振り返りを記録」で始まる録音のプロンプト群。通常のプリセットを使わず、この 3 本だけで対話と抽出が決まる。",
  },
  {
    id: "search",
    label: "商談検索",
    description:
      "商談の自然言語検索。クエリ解釈 → リランク → 回答生成の 3 段で動く。",
  },
  {
    id: "report",
    label: "顧客レポート",
    description:
      "Unizo 顧客レポートのカテゴリ別プロンプト。8 カテゴリ × 3 種別（サマリ生成／口コミ抽出／対話入力）で 24 本。",
  },
];

/**
 * 既定の機能（`/onp-settings` の着地先・不正スラッグの寄せ先・サブナビのリンク先）。
 *
 * **必ず先頭の機能から導出する。** 直書きすると並び替えたときに
 * 「左端のボタンと着地先が食い違う」壊れ方をする。
 */
export const DEFAULT_PROMPT_FEATURE: PromptFeature =
  PROMPT_FEATURES[0]?.id ?? "recording";

/**
 * 機能 → その機能に属する settingId（**画面の並び順そのまま**）。
 *
 * 顧客レポート（report）は 8 カテゴリ × 3 種別から自動生成するためここには含めない。
 */
const FEATURE_MEMBERS = {
  recording: [
    "PRODUCT_BASE",
    "COMPANY_SEARCH",
    "INDIVIDUAL_COMPANY_SEARCH",
    "DICTIONARY_TEMPLATE",
    "GROUP_CONTEXT",
    "COPY_MODE_COMPANY",
    "COPY_MODE_INDIVIDUAL",
    "CHARACTER_TONE",
  ],
  summary: [
    "GROUP_SUMMARY_GENERATION",
    "GROUP_SUMMARY_GENERATION_AI",
    "ACTIVITY_SUMMARY_BODY",
    "ACTIVITY_SUMMARY_HIGHLIGHT",
    "TEAM_SUMMARY_BODY",
  ],
  assist: [
    "SPEED_MODE_GUIDE_GENERATION",
    "PRESET_DRAFT_BUILD",
    "PRESET_VOICE_EDIT",
    "MEETING_COPY_SUMMARY",
    "COMPANY_NAME_KANA_SUGGESTION",
  ],
  // 実行順（抽出相 → チェック相 → 第3コール）に並べる。旧2パスは @deprecated だが
  // DB 上書きが残る環境の整理用に末尾へ残置。
  gatekeeper: [
    "MEETING_FIELD_EXTRACTION",
    "MEETING_GLOBAL_CHECK",
    "HEARING_NOTES_EXTRACTION",
    "TRANSCRIPT_CLEAN",
    "INDIVIDUAL_PROFILE_EXTRACTION",
    "CONTACT_SIMILARITY_MATCH",
    "MEETING_COMPLETION",
    "MEETING_ITEM_CHECK",
  ],
  minutes: ["MINUTES_STRUCTURE", "MINUTES_TRANSCRIBE", "MINUTES_CLEAN"],
  // 対話（本体）→ 議事録の埋め込み → 録音後の抽出、の実行順
  review: [
    "MINUTES_REVIEW_PROMPT",
    "MINUTES_REVIEW_CONTEXT",
    "MINUTES_REVIEW_EXTRACTION",
  ],
  // 解釈 → リランク → 回答生成の実行順
  search: ["MEETING_NL_SEARCH", "MEETING_NL_RERANK", "MEETING_NL_ANSWER"],
  base: ["LAYER1", "LAYER2"],
} as const satisfies Record<
  Exclude<PromptFeature, "report">,
  readonly ONPPromptSettingId[]
>;

/**
 * **全 settingId がいずれかの機能に載っていること**を型で保証する。
 * 未配置のキーがあると `UnplacedSettingId` が `never` にならず、下の型でコンパイルエラーになる。
 */
type PlacedSettingId =
  (typeof FEATURE_MEMBERS)[Exclude<PromptFeature, "report">][number];
type UnplacedSettingId = Exclude<ONPPromptSettingId, PlacedSettingId>;
type AssertNever<T extends never> = T;
export type PromptFeatureCoverage = AssertNever<UnplacedSettingId>;

/**
 * settingId → 機能（`FEATURE_MEMBERS` から導出。二重管理しない）。
 *
 * 上の `AssertNever` で全キーの網羅が保証されているので、
 * `Record<ONPPromptSettingId, ...>`（= 引き当てが必ず成功する型）として扱ってよい。
 */
const SETTING_FEATURE = Object.fromEntries(
  Object.entries(FEATURE_MEMBERS).flatMap(([feature, ids]) =>
    (ids as readonly string[]).map((id) => [id, feature as PromptFeature]),
  ),
) as Record<ONPPromptSettingId, PromptFeature>;

export interface PromptCatalogEntry {
  settingId: string;
  title: string;
  /** カード一覧と編集ペインに出す説明。何に効くのか・プレースホルダ・注意点を書く */
  description: string;
  feature: PromptFeature;
  /** 顧客レポートのみ: カテゴリ名（カード上のバッジ） */
  categoryLabel?: string;
  /** ハッシュ一覧の粗いグループ分け（機能から導出） */
  group: PromptCatalogGroup;
  /** 該当プロンプトを開く編集画面のパス */
  editPath: string;
}

export type PromptCatalogGroup = "ONP基盤" | "商談記録" | "顧客レポート";

function groupOf(feature: PromptFeature): PromptCatalogGroup {
  if (feature === "base") return "ONP基盤";
  if (feature === "report") return "顧客レポート";
  return "商談記録";
}

export function promptEditPath(
  feature: PromptFeature,
  settingId: string,
): string {
  return `/onp-settings/${feature}?id=${settingId}`;
}

/**
 * settingId → 表示名と説明。
 *
 * `Record<..., ...>` で全キー必須。説明は「何に効くか」「どの条件で注入されるか」
 * 「置換されるプレースホルダ」「書くときの注意」を書く。運用者はここしか読まない。
 */
const SETTING_LABELS: Record<
  ONPPromptSettingId,
  { title: string; description: string }
> = {
  LAYER1: {
    title: "Layer 1 (ONP基本ルール)",
    description:
      "音声 AI の基本的な振る舞いルール。全録音プロンプトの土台になるため、変更は全機能に波及する。",
  },
  LAYER2: {
    title: "Layer 2 (対話形式ルール)",
    description:
      "対話フローの制御ルール。Layer 1 の直後に置かれ、こちらも全録音プロンプトに効く。",
  },
  PRODUCT_BASE: {
    title: "商談記録ベースルール (Layer 3a)",
    description:
      "プリセットを持たないユーザ（既定プリセット）の録音プロンプト本体。ツール実行ルール・取得項目・初回動作を含む。**上書きは全文置換**で、プリセットを持つユーザには注入されない。恒久的な変更はソースへ入れて上書きは削除すること。",
  },
  COMPANY_SEARCH: {
    title: "法人検索ルール",
    description: "法人名の正規化・検索ルール。",
  },
  INDIVIDUAL_COMPANY_SEARCH: {
    title: "個人検索ルール（法人検索ルールの個人版）",
    description:
      "プリセットの情報種別が「個人」かつ個人検索が有効なときに、法人検索ルールの代わりに注入される。録音中に個人マスタを氏名照合し、登録済みの属性（年齢・収入等）を聞き直さないための指示。",
  },
  DICTIONARY_TEMPLATE: {
    title: "辞書テンプレート",
    description: "{entries} がエントリで置換される。",
  },
  GROUP_CONTEXT: {
    title: "続き記録 埋め込みプロンプト",
    description:
      "グループコンテキスト（過去商談要約）を録音プロンプトに注入する際のテンプレート。",
  },
  COPY_MODE_COMPANY: {
    title: "コピーモード指示（法人）",
    description:
      "法人商談をコピーして記録するときの指示部。データ部（✅法人名/✅担当者の実値）はコードが自動生成し、この本文がその下に入る。条件ブロック [[CONTACTS_FIXED]]（担当者あり・追加なし）/ [[CONTACTS_NEW]]（担当者あり・追加あり）/ [[NO_CONTACTS]]（担当者なし）で出し分け。プレースホルダ {company} {contactNames} {contactFieldLabel}",
  },
  COPY_MODE_INDIVIDUAL: {
    title: "コピーモード指示（個人）",
    description:
      "個人商談をコピーして記録するときの指示部。引き継いだお客様情報（氏名・年齢・職業等）は聞き直さず、「変わったこと・新しく分かったこと」だけを1回確認して商談内容へ進む。データ部はコードが自動生成。プレースホルダ {customerInfo}",
  },
  CHARACTER_TONE: {
    title: "対話キャラクター トーン指示",
    description:
      "全テナントの録音プロンプトで L2 の直後に必ず注入される（PRODUCT_DEV-716 でフラグ撤去）。Realtime API に声の高さを変えるパラメータは無いため、話し方の雰囲気はこのプロンプトで制御する。**トーン指示そのものを AI が地の文で喋らないよう明記すること**（PRODUCT_DEV-612 で実際に漏れた）。",
  },
  GROUP_SUMMARY_GENERATION: {
    title: "グループ要約プロンプト（人間用）",
    description: "商談グループの自動要約生成に使用（UI 表示用）。",
  },
  GROUP_SUMMARY_GENERATION_AI: {
    title: "グループ要約プロンプト（AI記録用）",
    description:
      "人間用要約を構造化テキストに変換する（続き記録の AI コンテキスト用）。",
  },
  ACTIVITY_SUMMARY_BODY: {
    title: "活動サマリ 本文プロンプト",
    description:
      "日報/週報/月報の本文下書きを生成する。入力は商談の extractedData とタイトル・顧客名まで（文字起こし全文は渡さない）。集計値はコード側が渡すので数え直させない。評価・順位づけ・煽りを書かせないこと。",
  },
  ACTIVITY_SUMMARY_HIGHLIGHT: {
    title: "活動サマリ ハイライトプロンプト",
    description:
      "顧客ごとの要点を JSON で抽出する。入力に無い顧客名・商談 ID はコード側のガードで落とすため、実在の顧客名をそのまま使うよう指示すること。",
  },
  TEAM_SUMMARY_BODY: {
    title: "部のまとめプロンプト（管理者ビュー）",
    description:
      "部署単位の活動をまとめ、管理者ビューの先頭に出す。**この文章はそのまま Amazon Polly が読み上げる**ので、箇条書き・記号・括弧書き・マークダウンを使わせないこと。人の評価・順位づけ・煽りも書かせない（件数の少ない人を責める文章にしない）。集計値はコード側が渡すので数え直させない。長くすると音声が伸び、Polly の 1 リクエスト 3,000 文字を超えると分割合成になる。",
  },
  SPEED_MODE_GUIDE_GENERATION: {
    title: "自由記録モード ガイド生成プロンプト",
    description:
      "プリセットのフィールド定義からガイドブロックを自動生成する。{fields} がフィールド定義で置換される。",
  },
  PRESET_DRAFT_BUILD: {
    title: "プリセットAI作成プロンプト",
    description:
      "音声で話した内容からプリセット草案と確認質問を生成する。{fieldCatalog}（共通項目カタログ）/{conversation}（発話ログ）/{previousDraft}/{round}/{maxRounds}/{antipatterns}/{structure} がコード側で置換される。",
  },
  PRESET_VOICE_EDIT: {
    title: "プリセット音声修正プロンプト",
    description:
      "既存プリセットへの「ここをこう直したい」から編集操作（ops）を生成する。プリセット全体は返させないので、言われていない箇所は変わらない。{currentPreset}（今のプリセットの要約。ルール番号 A1/R1/E1 がそのまま操作の index）/{fieldCatalog}（共通項目カタログ）/{instruction}（発話）がコード側で置換される。記録の仕方を台本へ入れない・終了の合図を消させないの担保はコード側にあるので本文で説得しないこと。",
  },
  MEETING_COPY_SUMMARY: {
    title: "CRMコピー文 文字数上限要約プロンプト",
    description:
      "商談詳細「コピーする」のテキストがプリセットの文字数上限（例: 連携先 CRM の上限が 700 文字）を超えたとき、上限以内に要約する。{maxLength} が目標文字数で置換される。URL 行は一字一句保持・原文にない情報の創作は禁止。",
  },
  COMPANY_NAME_KANA_SUGGESTION: {
    title: "法人名カナ候補生成プロンプト",
    description:
      "gBizInfo 側にヨミガナが無い法人について、法人名からカタカナの読み仮名を推定する。「株式会社」等の法人種別は読み仮名に含めない。出力は読み仮名のみ。",
  },
  MEETING_FIELD_EXTRACTION: {
    title: "項目抽出（GateKeeper 抽出相）プロンプト",
    description:
      "対話ログからフィールド（サブフィールド含む）単位で焦点抽出する system プロンプト（gpt-5.4-mini・グループ並列）。{dictionarySection} のみ動的置換。対象項目/既存データ/守備範囲外の他項目は実行時に対話ログの後ろへ別メッセージで付与される。",
  },
  MEETING_GLOBAL_CHECK: {
    title: "グローバルチェック（GateKeeper チェック相）プロンプト",
    description:
      "抽出済みの全項目を対話ログと突き合わせ、null 救済・配置是正・消失防止（備考 OK/消失 NG）・過剰重複整理を行う system プロンプト（gpt-5.4・常時1コール）。{fieldList}/{currentData}/{dictionarySection} が動的置換される。",
  },
  HEARING_NOTES_EXTRACTION: {
    title: "ヒアリングメモ抽出（GateKeeper 第3コール）プロンプト",
    description:
      "チェック相の後に1回実行し、「◯◯さんのお話では」のように明示的に特定人物へ帰属された発言のうち既存の記録項目に当てはまらないものを抽出する（gpt-5.4-mini）。個人商談は「その他ヒアリングできた事項」へ、法人商談は企業担当者のメモ欄へ格納。全テナントで実行（PRODUCT_DEV-716 でフラグ撤去）。{dictionarySection} のみ動的置換。",
  },
  TRANSCRIPT_CLEAN: {
    title: "音声ログ整形プロンプト（フィラー・ハルシネーション除去）",
    description:
      "対話ログのユーザ発話を整形する。フィラーワード除去・辞書正規化に加え、動画・字幕由来の定型ハルシネーション（「ご視聴ありがとうございました」等）を除去する。{dictionarySection} が動的値で置換される。",
  },
  INDIVIDUAL_PROFILE_EXTRACTION: {
    title: "個人プロファイル抽出プロンプト（顧客マスタ登録）",
    description:
      "プリセットに customer_info を含む「個人向け商談」の完了時に、対話ログと抽出済みデータから顧客マスタ「個人」の静的カラム（職業・特徴・年齢・収入・家族構成）を抽出する。{conversationLog} / {extractedData} が動的置換される。as-of 基準日はサーバ側（完了日）が付与するため、日付は出力させないこと。",
  },
  CONTACT_SIMILARITY_MATCH: {
    title: "担当者 類似判定プロンプト",
    description:
      "商談の本登録（reviewed=true 確定）直前に、編集後の担当者リストと顧客マスタの既存担当者リストを突き合わせ、同一人物候補を JSON で返す。候補があるときだけフロントが統合モーダルを出す。similarity は high/medium のみ返し、low は出さないこと。",
  },
  MEETING_COMPLETION: {
    title: "【旧】商談データ補完（GateKeeper Pass1）プロンプト",
    description:
      "【@deprecated PRODUCT_DEV-589】旧2パス方式の補完プロンプト。新フローでは未使用（MEETING_FIELD_EXTRACTION へ移行）。DB 上書きが残る環境の整理用。{fieldList}/{normalizedData}/{dictionarySection}",
  },
  MEETING_ITEM_CHECK: {
    title: "【旧】項目チェック（GateKeeper Pass2）プロンプト",
    description:
      "【@deprecated PRODUCT_DEV-589】旧2パス方式の項目チェック。新フローでは未使用（MEETING_GLOBAL_CHECK へ移行）。{fieldList}/{expandedData}/{dictionarySection}",
  },
  MINUTES_STRUCTURE: {
    title: "議事録 構造化プロンプト（要約/決定事項/Nextアクション）",
    description:
      "議事録の文字起こし全文を分析し、タイトル・AI 要約・決定事項・Next アクションを抽出する。finalize worker が全文へ1回だけ実行する。",
  },
  MINUTES_TRANSCRIBE: {
    title: "議事録 チャンク文字起こしプロンプト（文脈/語彙ヒント）",
    description:
      "議事録の音声チャンクを文字起こしする際の文脈・語彙ヒント。chunk worker が基底プロンプトに使用し、前チャンク末尾を連結する。無音・雑音は推測で補わない。",
  },
  MINUTES_CLEAN: {
    title: "議事録 文字起こしクリーニングプロンプト（Gatekeeper）",
    description:
      "議事録の生文字起こしをフィラー・言い直し・無音由来のハルシネーション除去できれいにする。意味内容は変えず推測を混ぜない。{dictionarySection} が動的値で置換される（全テナントで適用）。",
  },
  MINUTES_REVIEW_PROMPT: {
    title: "振り返り 対話プロンプト（ロール・ルール・会話例）",
    description:
      "議事録詳細の「商談の振り返りを記録」で始まる録音の本体プロンプト。通常のプリセット（項目定義）は使わず、この本文がまるごとプリセット相当（Layer 3a）になる。議事録に書いてある内容は聞き返させず、その場の相手の反応（声色・表情・目線・仕草・場の空気）だけを1問ずつ聞き出すよう書くこと。プレースホルダなし。",
  },
  MINUTES_REVIEW_CONTEXT: {
    title: "振り返り 議事録の埋め込みプロンプト",
    description:
      "振り返り対象の議事録を録音プロンプトへ差し込むテンプレート（Layer 3b、「続き記録 埋め込みプロンプト」と同じ位置づけ）。{minutesTitle} {minutesDate} {summary} {decisions} {nextActions} がコード側で置換される（決定事項/Next アクションは箇条書き、空なら「（記載なし）」）。",
  },
  MINUTES_REVIEW_EXTRACTION: {
    title: "振り返り 抽出プロンプト（GateKeeper 項目説明）",
    description:
      "録音後の GateKeeper が「振り返り（相手の反応・様子）」欄へ何を書くかの指示。合成プリセットの項目説明としてそのまま抽出プロンプトに載る。会議で決まったこと・話した内容そのものは議事録側にあるため守備範囲外と明記すること。プレースホルダなし。",
  },
  MEETING_NL_SEARCH: {
    title: "商談 自然言語検索 クエリ解釈プロンプト",
    description:
      "自然言語の検索クエリを商談検索フィルタの JSON（MeetingFilterInterpretationSchema 準拠）へ翻訳する。検索パイプラインの第1段。",
  },
  MEETING_NL_RERANK: {
    title: "商談 自然言語検索 リランクプロンプト",
    description:
      "元クエリと候補商談のサマリ（最大50件）を受け取り、関連度順に並べた商談 ID 配列を返す。検索パイプラインの第2段。",
  },
  MEETING_NL_ANSWER: {
    title: "商談 自然言語検索 回答生成プロンプト",
    description:
      "検索結果を根拠に回答文を生成する（第3段）。PRODUCT_DEV-716 で全テナントが議事録/メモ/ToDo も引用できる横断版に一本化された（商談のみの従来版は削除）。⚠ 横断化以前の文言で上書きしたままだと横断引用が出ないため、上書きを置いている場合は本文を横断版へ更新すること。",
  },
};

/** 顧客レポート種別ラベル */
const REPORT_TYPE_LABELS: Record<
  ReportPromptType,
  { title: string; description: string }
> = {
  SUMMARY: {
    title: "サマリ生成",
    description:
      "全商談＋エントリーからこのカテゴリのインサイトを生成するプロンプト。",
  },
  EXTRACT: {
    title: "口コミ抽出",
    description:
      "1 商談のトランスクリプトからこのカテゴリのエントリーを切り出すプロンプト。",
  },
  DIALOGUE: {
    title: "対話入力",
    description:
      "音声入力時に AI がこのカテゴリについて質問するプロンプト（短く）。",
  },
};

function onpEntry(settingId: ONPPromptSettingId): PromptCatalogEntry {
  const feature = SETTING_FEATURE[settingId];
  const label = SETTING_LABELS[settingId];
  return {
    settingId,
    title: label.title,
    description: label.description,
    feature,
    group: groupOf(feature),
    editPath: promptEditPath(feature, settingId),
  };
}

/** ONP 系 35 件（機能ごとの並び順のまま連結） */
const ONP_ENTRIES: PromptCatalogEntry[] = PROMPT_FEATURES.filter(
  (f) => f.id !== "report",
).flatMap((f) =>
  (FEATURE_MEMBERS[f.id as Exclude<PromptFeature, "report">] as readonly ONPPromptSettingId[]).map(
    onpEntry,
  ),
);

/** 顧客レポート 24 件（8 カテゴリ × 3 種別） */
const REPORT_ENTRIES: PromptCatalogEntry[] = REPORT_CATEGORIES.flatMap((cat) =>
  REPORT_PROMPT_TYPES.map((type) => ({
    settingId: getReportPromptSettingId(type, cat.id),
    title: `${cat.title} - ${REPORT_TYPE_LABELS[type].title}`,
    description: REPORT_TYPE_LABELS[type].description,
    feature: "report" as const,
    categoryLabel: cat.title,
    group: "顧客レポート" as const,
    editPath: promptEditPath("report", getReportPromptSettingId(type, cat.id)),
  })),
);

const BASE_CATALOG: PromptCatalogEntry[] = [...ONP_ENTRIES, ...REPORT_ENTRIES];

/**
 * 保険: contract に存在するが BASE_CATALOG に載っていない settingId を末尾に補完する。
 * `FEATURE_MEMBERS` の型ガードで ONP 側は落ちない想定だが、レポート側の id 生成規則が
 * 変わったときに一覧から黙って消えるのを防ぐ。
 */
function buildCatalog(): PromptCatalogEntry[] {
  const known = new Set(BASE_CATALOG.map((e) => e.settingId));
  const extra: PromptCatalogEntry[] = [];

  for (const id of ONPPromptSettingIdSchema.options) {
    if (!known.has(id)) {
      extra.push({
        settingId: id,
        title: id,
        description: "（カタログ未登録）",
        feature: "recording",
        group: "商談記録",
        editPath: promptEditPath("recording", id),
      });
      known.add(id);
    }
  }
  for (const id of ALL_REPORT_PROMPT_SETTING_IDS) {
    if (!known.has(id)) {
      extra.push({
        settingId: id,
        title: id,
        description: "（カタログ未登録）",
        feature: "report",
        group: "顧客レポート",
        editPath: promptEditPath("report", id),
      });
      known.add(id);
    }
  }

  return [...BASE_CATALOG, ...extra];
}

/** 全プロンプトの固定順カタログ */
export const PROMPT_CATALOG: PromptCatalogEntry[] = buildCatalog();

/** 機能 → その機能のカタログ行（表示順） */
export function catalogByFeature(feature: PromptFeature): PromptCatalogEntry[] {
  return PROMPT_CATALOG.filter((e) => e.feature === feature);
}

/** settingId → カタログ行 */
export function catalogEntryOf(
  settingId: string,
): PromptCatalogEntry | undefined {
  return PROMPT_CATALOG.find((e) => e.settingId === settingId);
}

/** 表示順を保ったグループ一覧（ハッシュ一覧で使用） */
export const PROMPT_CATALOG_GROUPS: PromptCatalogGroup[] = [
  "ONP基盤",
  "商談記録",
  "顧客レポート",
];
